/**
 * DashboardGrid — react-grid-layout ResponsiveGridLayout rendering a
 * dashboard's widgets at their stored gridX/Y/W/H. In view mode
 * (editMode=false, the default) isDraggable/isResizable are hard false and
 * each widget renders through WidgetHost directly. In edit mode: RGL's
 * drag/resize are scoped to `.widget-drag-handle` (draggableHandle), a
 * dotted 12-col backdrop renders behind the grid, and each widget is
 * wrapped in EditableWidgetCard (the drag-handle + overflow-menu overlay).
 * Staging is driven off onDragStop/onResizeStop (not RGL's own
 * onLayoutChange event, which also fires on mount, after every compaction,
 * and mid-drag) — those two only fire once per genuine user interaction, at
 * its end, with the FINAL layout. `onLayoutChange` is forwarded the final
 * layout when it diverges from the widgets' persisted grid positions, or
 * `null` when the interaction round-tripped back to the persisted positions
 * (e.g. drag away then drag back) — the parent must clear any stale staged
 * value in that case rather than keep an earlier intermediate position. The
 * parent (DashboardPage, T8) stages genuine changes and commits as one batch
 * PUT on "Done" — DashboardGrid itself holds no staged state. Uses the
 * `/legacy` v1-compatible API surface (plan Architecture point 4).
 *
 * Editing is pinned to a single 12-col breakpoint regardless of viewport
 * (design brief NON-GOALS: "responsive-per-breakpoint EDITING... editing
 * desktop-first") — RGL's default responsive breakpoints/cols (md=10 cols
 * below 1200px, sm=6, ...) would otherwise clamp a 12-col item's x/w the
 * moment the app's content column drops under ~1312px, corrupting the
 * canonical layout on the very first drag. View mode keeps the normal
 * responsive breakpoints so the read-only canvas still reflows.
 *
 * AUTO_HEIGHT_BUILTINS (ACCOUNTS_TABLE / PROJECT_RAG_TABLE, gridConstants.ts)
 * get a CONTENT-driven h instead of their stored gridH: `rowCounts` state,
 * fed by the onRowCount WidgetHost passes down to those two Hosts only,
 * drives gridRowsForListRows in the layout useMemo below. This is
 * presentational (never written back to the widget's stored grid_h) and
 * must never be mistaken for a user drag/resize — handleInteractionEnd
 * normalizes these two kinds' h back to their persisted gridH before
 * diffing, so a genuine OTHER widget's drag/resize ending can't pick up an
 * auto-height delta and stage a phantom "unsaved changes".
 */
import { useCallback, useMemo, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import type { Layout } from 'react-grid-layout';
import type { DashboardWidgetResponse } from '@/types/domain';
import { WidgetHost } from './WidgetHost';
import { EditableWidgetCard } from './EditableWidgetCard';
import {
  ROW_HEIGHT, GRID_MARGIN, WIDGET_MIN_W, widgetMinH,
  AUTO_HEIGHT_BUILTINS, MIN_LIST_GRID_ROWS, gridRowsForListRows,
} from './gridConstants';

const ResponsiveGridLayout = WidthProvider(Responsive);

const VIEW_BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const VIEW_COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const EDIT_BREAKPOINTS = { lg: 0 };
const EDIT_COLS = { lg: 12 };

export interface DashboardGridProps {
  widgets: DashboardWidgetResponse[];
  accountId: string | null;
  editMode?: boolean;
  onLayoutChange?: (layout: Layout | null) => void;
  onRename?: (widget: DashboardWidgetResponse) => void;
  onMoveResize?: (widget: DashboardWidgetResponse) => void;
  onRemove?: (widget: DashboardWidgetResponse) => void;
  /** M4 Configure surface — threaded straight through to EditableWidgetCard. */
  onConfigure?: (widget: DashboardWidgetResponse) => void;
}

/**
 * True only if some item's x/y/w/h actually diverges from the widget it
 * corresponds to. Used to swallow RGL's mount-time/post-compaction
 * onLayoutChange echoes (see the file banner above) so only a genuine
 * drag/resize stages a change.
 */
function layoutsDiffer(layout: Layout, widgets: DashboardWidgetResponse[]): boolean {
  const byId = new Map(widgets.map((w) => [w.id, w]));
  return layout.some((item) => {
    const w = byId.get(item.i);
    return !w || w.gridX !== item.x || w.gridY !== item.y || w.gridW !== item.w || w.gridH !== item.h;
  });
}

/**
 * Resets AUTO_HEIGHT_BUILTINS entries' h back to their persisted gridH
 * before diffing/staging (layoutsDiffer, handleInteractionEnd below). Their
 * rendered h is presentational — content-driven via gridRowsForListRows,
 * never a user drag/resize — so a genuine OTHER widget's interaction ending
 * must not stage a phantom height change for these two kinds (and have it
 * persisted as their new stored grid_h on Save). Only h is touched: x/y/w
 * stay whatever RGL reports, since position/width ARE still user-controlled
 * for these tiles — only their height is "not meant to be hand-sized".
 */
function normalizeAutoHeight(layout: Layout, widgets: DashboardWidgetResponse[]): Layout {
  const byId = new Map(widgets.map((w) => [w.id, w]));
  return layout.map((item) => {
    const w = byId.get(item.i);
    if (w?.builtinRef && AUTO_HEIGHT_BUILTINS.has(w.builtinRef)) {
      return { ...item, h: w.gridH };
    }
    return item;
  });
}

export function DashboardGrid({
  widgets, accountId, editMode = false, onLayoutChange, onRename, onMoveResize, onRemove, onConfigure,
}: DashboardGridProps) {
  // Row counts AUTO_HEIGHT_BUILTINS Hosts report once their data resolves
  // (WidgetHost → AccountsTableHost/ProjectRagTableHost's onRowCount), keyed
  // by widget id. Absent until the first report, so the layout useMemo below
  // falls back to a pre-data reservation (see its own comment).
  const [rowCounts, setRowCounts] = useState<Record<string, number>>({});
  const handleRowCount = useCallback((id: string, n: number) => {
    setRowCounts((prev) => (prev[id] === n ? prev : { ...prev, [id]: n }));
  }, []);

  const layout: Layout = useMemo(
    () => widgets.map((w) => {
      const autoHeight = Boolean(w.builtinRef && AUTO_HEIGHT_BUILTINS.has(w.builtinRef));
      if (autoHeight) {
        // Content-driven: once the Host has reported a row count, gridH is
        // superseded entirely (gridConstants.ts) — a manual resize or a
        // stale stored value can never win over the real content height.
        // Before that first report, reserve exactly the MIN floor (not the
        // stored gridH): the height only ever grows from MIN toward content,
        // never shrinks — so the common sparse case (1 row → MIN) settles with
        // ZERO jump, and a denser table grows once, bottom-anchored and benign.
        const h = w.id in rowCounts ? gridRowsForListRows(rowCounts[w.id]) : MIN_LIST_GRID_ROWS;
        return {
          i: w.id, x: w.gridX, y: w.gridY, w: w.gridW, h,
          minW: WIDGET_MIN_W, minH: MIN_LIST_GRID_ROWS,
          // Not meant to be hand-sized: the row count already determines h
          // every render, so hide the resize handle instead of letting a
          // drag silently get overridden back on the next render.
          isResizable: false,
        };
      }
      // Shared floors from gridConstants (also enforced by MoveResizeModal's
      // keyboard path), with the per-kind minH override for builtins whose
      // fixed-size content needs more vertical room (the donuts). Clamp a
      // stored-shorter h up to the kind floor: widgets added before a floor
      // existed (e.g. Capacity at the old h:3 palette default) must render
      // tall enough for their content contract, not clip it.
      return {
        i: w.id, x: w.gridX, y: w.gridY, w: w.gridW, h: Math.max(w.gridH, widgetMinH(w.builtinRef)),
        minW: WIDGET_MIN_W, minH: widgetMinH(w.builtinRef),
      };
    }),
    [widgets, rowCounts],
  );

  // Fires once per genuine drag/resize interaction, with the FINAL layout —
  // forwards it when it diverges from the persisted positions, or `null`
  // when the interaction reverted back to them (see file banner). Auto-
  // height entries are normalized back to their persisted gridH first: their
  // h is presentational, never a user drag/resize, so it must never be
  // mistaken for one and staged as an "unsaved change".
  function handleInteractionEnd(l: Layout) {
    const normalized = normalizeAutoHeight(l, widgets);
    onLayoutChange?.(layoutsDiffer(normalized, widgets) ? normalized : null);
  }

  return (
    <div style={{ position: 'relative' }}>
      {editMode && <div className="dashboard-grid-backdrop" aria-hidden="true" />}
      <ResponsiveGridLayout
        className={editMode ? 'dashboard-grid dashboard-grid-editing' : 'dashboard-grid'}
        layouts={{ lg: layout }}
        breakpoints={editMode ? EDIT_BREAKPOINTS : VIEW_BREAKPOINTS}
        cols={editMode ? EDIT_COLS : VIEW_COLS}
        rowHeight={ROW_HEIGHT}
        margin={GRID_MARGIN}
        isDraggable={editMode}
        isResizable={editMode}
        draggableHandle={editMode ? '.widget-drag-handle' : undefined}
        // resizeHandles: react-grid-layout's /legacy shim always renders a
        // .react-resizable-handle DOM node per resizeHandles entry (only
        // CSS-hiding it when isResizable is false), so view mode must pass an
        // EMPTY handle set to keep zero resize affordances; edit mode restores
        // the default ['se'] corner handle alongside isResizable={true}.
        resizeHandles={editMode ? ['se'] : []}
        compactType="vertical"
        onDragStop={editMode ? handleInteractionEnd : undefined}
        onResizeStop={editMode ? handleInteractionEnd : undefined}
      >
        {widgets.map((widget, index) => {
          const autoHeight = Boolean(widget.builtinRef && AUTO_HEIGHT_BUILTINS.has(widget.builtinRef));
          return (
            // The entrance-stagger class lives on an INNER wrapper, never on
            // the div RGL clones directly — see plan Architecture point 5.
            // auto-height-widget (dashboardGrid.css) narrows THIS item's own
            // transition-property to transform-only, so its content-driven
            // height (see file banner) settles instantly on data arrival
            // instead of animating — RGL's shipped stylesheet transitions
            // height by default. Harmless on every other widget: the class
            // is simply absent.
            <div key={widget.id} className={autoHeight ? 'auto-height-widget' : undefined}>
              <div className="reveal-i" style={{ height: '100%', ['--i' as unknown as string]: Math.min(index, 14) }}>
                {editMode ? (
                  <EditableWidgetCard
                    widget={widget}
                    accountId={accountId}
                    row={widget.gridY + 1}
                    col={widget.gridX + 1}
                    onRename={() => onRename?.(widget)}
                    onMoveResize={() => onMoveResize?.(widget)}
                    onRemove={() => onRemove?.(widget)}
                    onConfigure={() => onConfigure?.(widget)}
                  />
                ) : (
                  <WidgetHost
                    widget={widget}
                    accountId={accountId}
                    onRowCount={autoHeight ? (n: number) => handleRowCount(widget.id, n) : undefined}
                  />
                )}
              </div>
            </div>
          );
        })}
      </ResponsiveGridLayout>
    </div>
  );
}
